import { Calendar, User, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { urlFor } from '@/lib/sanity'
import { useLanguage } from "@/contexts/LanguageContext";

const BlogResultsSection = ({
  blogs,
  totalCount,
  searchTerm,
  selectedCategory,
  onClearFilters,
  columns= "lg:grid-cols-3"
}) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const isAllCategory = selectedCategory === "All" || selectedCategory === "الكل";

  const formatReadTime = (readTime?: string) => {
    if (!readTime) return "—";
    if (!isRTL) return readTime;

    const minutes = Number.parseInt(readTime, 10);
    if (Number.isNaN(minutes)) return readTime;

    const formattedMinutes = new Intl.NumberFormat("ar-EG").format(minutes);
    const pluralCategory = new Intl.PluralRules("ar-EG").select(minutes);

    if (pluralCategory === "one") return "دقيقة واحدة للقراءة";
    if (pluralCategory === "two") return "دقيقتان للقراءة";
    if (pluralCategory === "few") return `${formattedMinutes} دقائق للقراءة`;
    return `${formattedMinutes} دقيقة للقراءة`;
  };

  return (
    <section id="blog-results" dir={isRTL ? "rtl" : "ltr"}>
      <div className="mb-8 text-center text-gray-600 dark:text-gray-300">
        {language === "ar" ? (
          <>
            يتم عرض {blogs.length} من أصل {totalCount ?? 0} مقالة
            {!isAllCategory && <> في {selectedCategory}</>}
            {searchTerm && <> للبحث عن "{searchTerm}"</>}
          </>
        ) : (
          <>
            Showing {blogs.length} of {totalCount ?? 0} article{totalCount !== 1 ? "s" : ""}
            {!isAllCategory && <> in {selectedCategory}</>}
            {searchTerm && <> for "{searchTerm}"</>}
          </>
        )}
      </div>

      {blogs.length > 0 ? (
        <div className={`grid grid-cols-1 md:grid-cols-2 ${columns} gap-8`}>
          {blogs.map((blog) => (
            <Card
              key={blog.id || blog.slug?.current}
              dir={isRTL ? "rtl" : "ltr"}
              className={`h-full flex flex-col hover:shadow-lg transition-shadow ${isRTL ? "text-right" : "text-left"}`}
            >
              <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-t-lg relative overflow-hidden">
                {blog.mainImage ? (
                  <img
                    src={urlFor(blog.mainImage)}
                    alt={blog.title || "Blog image"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-sm text-gray-400">
                    No Image
                  </div>
                )}
                <div className={`absolute top-3 flex flex-wrap gap-2 ${isRTL ? "right-3" : "left-3"}`}>
                  {(blog.categories ?? []).map((cat) => (
                    <Badge
                      key={cat?.id || cat?.title}
                      className="bg-nexus-teal text-white text-xs"
                    >
                      {cat?.title || "No category"}
                    </Badge>
                  ))}
                </div>
              </div>
              <CardHeader className="flex-grow">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
                  <div className="flex min-w-0 items-center gap-1">
                    <User className="h-4 w-4 shrink-0" />
                    {blog.author?.name || "Unknown"}
                  </div>
                  <div className="flex min-w-0 items-center gap-1">
                    <Calendar className="h-4 w-4 shrink-0" />
                    {blog.publishedAt
                      ? new Date(blog.publishedAt).toLocaleDateString(isRTL ? "ar-EG" : "en-US")
                      : "—"}
                  </div>
                </div>
                <CardTitle className="text-xl mb-2 line-clamp-2">
                  {blog.title || "Untitled Post"}
                </CardTitle>
                <CardDescription className="line-clamp-3">
                  {blog.excerpt || "No excerpt available."}
                </CardDescription>
                <div className="flex flex-wrap gap-2 mt-4">
                  {(blog.tags ?? []).slice(0, 3).map((tag, index) => (
                    <Badge key={tag || index} variant="secondary" className="text-xs">
                      {tag || "Tag"}
                    </Badge>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {language === "ar" ? (
                    <>
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="text-nexus-teal hover:text-nexus-teal/80"
                      >
                        <Link to={`/blog/${blog.id || "#"}`}>
                          {t("blog_read_more")}
                          <ArrowRight className="mr-2 h-4 w-4 transform rotate-180" />
                        </Link>
                      </Button>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatReadTime(blog.readTime)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatReadTime(blog.readTime)}
                      </span>
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="text-nexus-teal hover:text-nexus-teal/80"
                      >
                        <Link to={`/blog/${blog.id || "#"}`}>
                          {t("blog_read_more")}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <h3 className="text-2xl font-semibold text-gray-600 dark:text-gray-300 mb-4">
            {t('noArticles_title')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {t('noArticles_description')}
          </p>
          <Button onClick={onClearFilters} variant="outline">
            {t('noArticles_clearButton')}
          </Button>
        </div>
      )}
    </section>
  );
};

export default BlogResultsSection;
